import {hasFeatureFlag} from '../../util/FeatureUtil';
import {
	getNotifications as getNovaNotifications,
	subscribeToNotifications as subscribeToNovaNotifications,
} from './novaNotificationStore';
import type {NovaNotificationEntry} from './novaNotificationStore';

export type TaggedNovaNotificationEntry = NovaNotificationEntry & {isNovaNotification: true};

function tagAsNova(entries: NovaNotificationEntry[]): TaggedNovaNotificationEntry[] {
	return entries.map(entry => ({...entry, isNovaNotification: true}));
}

// Both merge surfaces (the header popover and the fullscreen /my-profile/all-notifications page)
// need the same tagged snapshot for their initial state — bundled here so neither can drift from
// the other by, say, tagging on every render instead of once per change.
export function initialNovaNotifications(): TaggedNovaNotificationEntry[] {
	return hasFeatureFlag('nova_agent_invocation') ? tagAsNova(getNovaNotifications()) : [];
}

// Returns undefined when the flag is off, matching the caller's own "only unsubscribe if we ever
// subscribed" cleanup pattern (`if (this.novaUnsubscribe) this.novaUnsubscribe()`).
export function subscribeToMergedNovaNotifications(
	onChange: (entries: TaggedNovaNotificationEntry[]) => void
): (() => void) | undefined {
	if (!hasFeatureFlag('nova_agent_invocation')) return undefined;
	return subscribeToNovaNotifications(() => onChange(tagAsNova(getNovaNotifications())));
}

// A nova entry counts toward the header badge only if it arrived after the last time the
// notification menu was opened - opening clears the badge contribution without removing the
// entries themselves from the list, matching the native/ticket badges' own "opening clears it"
// behavior (nova's separate per-row dismiss is still what actually removes an entry). An
// overflow_summary entry stands in for several finished runs folded into one row once the display
// cap is hit, so it contributes its own count, not 1 - counting it as 1 would undercount the badge
// past the cap.
export function countUnseenNovaNotifications(entries: NovaNotificationEntry[], seenAt: number): number {
	return entries
		.filter(entry => entry.createdAt > seenAt)
		.reduce((total, entry) => total + (entry.kind === 'overflow_summary' ? entry.count : 1), 0);
}
